import { useEffect, useState } from "react";
import { CheckCircle2, Plus } from "lucide-react";
import { EmployerLayout } from "@/components/employer/portal/EmployerLayout";
import { apiFetch, formatApiError, formatDate, inputClass, Kicker, Label, PrimaryButton, Spinner } from "@/components/employer/portal/shared";

const EMPTY = { position: "", workers: 1, country: "", industry: "", qualifications: "", experience: "", start_date: "", salary: "", accommodation: "", transportation: "", talent_market: "", additional: "" };

export default function EmployerRequests() {
    const [items, setItems] = useState(null);
    const [form, setForm] = useState(EMPTY);
    const [busy, setBusy] = useState(false);
    const [saved, setSaved] = useState(false);
    const [error, setError] = useState("");

    const load = () => apiFetch("/employer/recruitment-requests").then((r) => (r.ok ? r.json() : [])).then(setItems);
    useEffect(() => { load(); }, []);
    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async () => {
        if (busy) return;
        setBusy(true);
        setError("");
        setSaved(false);
        const res = await apiFetch("/employer/recruitment-requests", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ ...form, workers: Number(form.workers) || 1 }),
        });
        if (res.ok) {
            setForm(EMPTY);
            setSaved(true);
            load();
        } else {
            const body = await res.json().catch(() => ({}));
            setError(formatApiError(body.detail, "Request could not be saved."));
        }
        setBusy(false);
    };

    return (
        <EmployerLayout title="Recruitment Requests" subtitle="Share broader workforce requirements with Afrinexa alongside individual vacancies.">
            <div className="grid gap-8 lg:grid-cols-[0.42fr_0.58fr]" data-testid="employer-requests-page">
                <section className="h-fit border border-ink/10 bg-ivory p-6">
                    <Kicker>New request</Kicker>
                    <div className="mt-6 space-y-4">
                        <div><Label>Position</Label><input data-testid="request-position" value={form.position} onChange={set("position")} className={inputClass} /></div>
                        <div className="grid grid-cols-2 gap-4">
                            <div><Label>Workers</Label><input data-testid="request-workers" type="number" min="1" value={form.workers} onChange={set("workers")} className={inputClass} /></div>
                            <div><Label>Country</Label><input data-testid="request-country" value={form.country} onChange={set("country")} className={inputClass} /></div>
                        </div>
                        <div><Label>Industry</Label><input data-testid="request-industry" value={form.industry} onChange={set("industry")} className={inputClass} /></div>
                        <div><Label>Experience</Label><input data-testid="request-experience" value={form.experience} onChange={set("experience")} className={inputClass} /></div>
                        <div><Label>Additional requirements</Label><textarea data-testid="request-additional" rows={4} value={form.additional} onChange={set("additional")} className={`${inputClass} resize-y`} /></div>
                        {error && <p data-testid="request-error" className="border-l-2 border-champagne pl-4 text-sm text-ink/70">{error}</p>}
                        <div className="flex items-center gap-3">
                            <PrimaryButton data-testid="request-submit" busy={busy} onClick={submit}><Plus size={14} /> Submit Request</PrimaryButton>
                            {saved && <span data-testid="request-saved" className="inline-flex items-center gap-1.5 text-sm text-forest"><CheckCircle2 size={15} /> Saved</span>}
                        </div>
                    </div>
                </section>
                <section>
                    {!items ? <Spinner /> : items.length === 0 ? <p className="border-t border-ink/10 py-12 text-sm text-ink/50">No recruitment requests yet.</p> : (
                        <div className="border-t border-ink/10" data-testid="requests-list">
                            {items.map((item) => (
                                <article key={item.id} data-testid={`request-${item.id}`} className="border-b border-ink/10 py-5">
                                    <div className="flex flex-wrap items-center justify-between gap-3">
                                        <h2 className="font-serif text-xl font-medium">{item.position}</h2>
                                        <span className="text-[10px] font-semibold uppercase tracking-[0.18em] text-champagne">{item.status}</span>
                                    </div>
                                    <p className="mt-2 text-sm text-ink/55">{item.workers} workers · {item.industry} · {item.country} · {formatDate(item.created_at)}</p>
                                </article>
                            ))}
                        </div>
                    )}
                </section>
            </div>
        </EmployerLayout>
    );
}
